import axios from "axios";
import { getApiUrl } from "../../src/utils/envConfig";
import {
  validateSecurityHeaders,
  getApiSecurityHeaders,
} from "../config/securityHeaders";
import { getAuthToken, refreshAuthToken } from "../utils/authUtils";
import { fetchUserData } from "./marketOverviewApi";
import { extractClientId } from "../utils/extractClientId";
import type { components } from "./generated/schema";

// Request/response contracts sourced from the FastAPI OpenAPI schema via
// openapi-typescript (npm run gen:api-types). These types are generated
// from the backend Pydantic models, so they can't drift from what the API
// actually accepts and returns — CI fails the build if schema.ts is stale.
type ActivePetFormulaResponse =
  components["schemas"]["ActivePetFormulaResponse"];
type CompanyBrandSubbrandTreeResponse =
  components["schemas"]["CompanyBrandSubbrandTreeResponse"];
type UpdatePetFormulaRequest = components["schemas"]["UpdatePetFormulaRequest"];
type UpdatePetFormulaResponse =
  components["schemas"]["UpdatePetFormulaResponse"];
type CountryVersionHistoryResponse =
  components["schemas"]["CountryVersionHistoryResponse"];
type AllVersionsResponse = components["schemas"]["AllVersionsResponse"];
type GetNameCompletionResponse =
  components["schemas"]["GetNameCompletionResponse"];
type UpdateNameCompletionRequest =
  components["schemas"]["UpdateNameCompletionRequest"];
type UpdateNameCompletionResponse =
  components["schemas"]["UpdateNameCompletionResponse"];
type NameCompletionStatusResponse =
  components["schemas"]["NameCompletionStatusResponse"];

// Fetch PET Formula active data
export const fetchOneFormulaActive = async (existingUserData = null, selectedWorkspace = null, authIdClient = null): Promise<any> => {
  try {
    let userData, idClient;

    if (existingUserData) {
      // Use existing user data from Redux
      userData = existingUserData;
      idClient = extractClientId(userData, selectedWorkspace, authIdClient);
    } else {
      // Fallback to fetching user data
      userData = await fetchUserData(true);
      idClient = extractClientId(userData, selectedWorkspace, authIdClient);
    }

    if (!idClient) {
      throw new Error("Client ID not found in user data");
    }

    // Build the API endpoint URL
    const url = `${getApiUrl()}/${idClient}/pet_formula/active`;

    // Make the API request
    const data = await makeAuthenticatedRequest(url, { method: "GET" });

    return data;
  } catch (error) {
    console.error("[fetchOneFormulaActive] Error fetching PET formula data:", error);
    throw new Error("Failed to fetch PET formula data: " + error.message);
  }
};

// Fetch brand tree data for a specific country with all companies
export const fetchBrandTree = async (countryCode, existingUserData = null, selectedWorkspace = null, authIdClient = null): Promise<CompanyBrandSubbrandTreeResponse> => {
  try {
    let userData, idClient;

    if (existingUserData) {
      // Use existing user data from Redux
      userData = existingUserData;
      idClient = extractClientId(userData, selectedWorkspace, authIdClient);
    } else {
      // Fallback to fetching user data
      userData = await fetchUserData(true);
      idClient = extractClientId(userData, selectedWorkspace, authIdClient);
    }

    if (!idClient) {
      throw new Error("Client ID not found in user data");
    }

    if (!countryCode) {
      throw new Error("Country code is required");
    }

    // Fetch the complete company-brand-subbrand tree in a single request
    const url = `${getApiUrl()}/client/${idClient}/country/${countryCode}/brands/subbrands/tree`;

    const data = await makeAuthenticatedRequest(url, { method: "GET" });
    return data;

  } catch (error) {
    console.error("[fetchBrandTree] Error fetching brand tree data:", error);
    // Re-throw the original error to preserve status code and other properties
    throw error;
  }
};

// Update PET Formula configuration
export const updateOneFormula = async (requestBody: any, existingUserData = null, selectedWorkspace = null, authIdClient = null): Promise<UpdatePetFormulaResponse> => {
  try {
    let userData, idClient;

    if (existingUserData) {
      // Use existing user data from Redux
      userData = existingUserData;
      idClient = extractClientId(userData, selectedWorkspace, authIdClient);
    } else {
      // Fallback to fetching user data
      userData = await fetchUserData(true);
      idClient = extractClientId(userData, selectedWorkspace, authIdClient);
    }

    if (!idClient) {
      throw new Error("Client ID not found in user data");
    }

    // Build the API endpoint URL
    const url = `${getApiUrl()}/${idClient}/pet_formula/update`;

    // Make the API request
    const data = await makeAuthenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify(requestBody)
    });

    return data;
  } catch (error) {
    console.error("[updateOneFormula] Error updating PET formula data:", error);
    // Re-throw the original error to preserve status code and other properties
    throw error;
  }
};

// Fetch version history for a specific country
export const fetchVersionHistory = async (countryCode, existingUserData = null, selectedWorkspace = null, authIdClient = null): Promise<any> => {
  try {
    let userData, idClient;

    if (existingUserData) {
      // Use existing user data from Redux
      userData = existingUserData;
      idClient = extractClientId(userData, selectedWorkspace, authIdClient);
    } else {
      // Fallback to fetching user data
      userData = await fetchUserData(true);
      idClient = extractClientId(userData, selectedWorkspace, authIdClient);
    }

    if (!idClient) {
      throw new Error("Client ID not found in user data");
    }

    if (!countryCode) {
      throw new Error("Country code is required");
    }

    // Build the API endpoint URL
    const url = `${getApiUrl()}/${idClient}/${countryCode}/pet_formula/version_history`;

    // Make the API request
    const data = await makeAuthenticatedRequest(url, { method: "GET" });

    return data;
  } catch (error) {
    console.error("[fetchVersionHistory] Error fetching version history:", error);
    throw new Error("Failed to fetch version history: " + error.message);
  }
};

// Fetch all done config versions for the client (max 3, newest first)
export const fetchAllVersions = async (existingUserData = null, selectedWorkspace = null, authIdClient = null): Promise<AllVersionsResponse> => {
  let userData, idClient;
  if (existingUserData) {
    userData = existingUserData;
    idClient = extractClientId(userData, selectedWorkspace, authIdClient);
  } else {
    userData = await fetchUserData(true);
    idClient = extractClientId(userData, selectedWorkspace, authIdClient);
  }
  if (!idClient) throw new Error("Client ID not found in user data");
  const url = `${getApiUrl()}/${idClient}/pet_formula/versions`;
  return await makeAuthenticatedRequest(url, { method: "GET" });
};

// Fetch the pipeline status for the latest formula version
export const fetchFormulaStatus = async (userData, selectedWorkspace): Promise<any> => {
  const idClient = extractClientId(userData, selectedWorkspace);
  const url = `${getApiUrl()}/${idClient}/pet_formula/status`;
  return await makeAuthenticatedRequest(url, { method: "GET" });
};

// Fetch active name completion weights for all countries
export const fetchNameCompletionActive = async (existingUserData = null, selectedWorkspace = null, authIdClient = null): Promise<GetNameCompletionResponse> => {
  let userData, idClient;
  if (existingUserData) {
    userData = existingUserData;
    idClient = extractClientId(userData, selectedWorkspace, authIdClient);
  } else {
    userData = await fetchUserData(true);
    idClient = extractClientId(userData, selectedWorkspace, authIdClient);
  }
  if (!idClient) throw new Error("Client ID not found in user data");
  const url = `${getApiUrl()}/${idClient}/name_completion/active`;
  return await makeAuthenticatedRequest(url, { method: "GET" });
};

// Update name completion weights (triggers pipeline re-run)
export const updateNameCompletion = async (requestBody: UpdateNameCompletionRequest, existingUserData = null, selectedWorkspace = null, authIdClient = null): Promise<UpdateNameCompletionResponse> => {
  let userData, idClient;
  if (existingUserData) {
    userData = existingUserData;
    idClient = extractClientId(userData, selectedWorkspace, authIdClient);
  } else {
    userData = await fetchUserData(true);
    idClient = extractClientId(userData, selectedWorkspace, authIdClient);
  }
  if (!idClient) throw new Error("Client ID not found in user data");
  const url = `${getApiUrl()}/${idClient}/name_completion/update`;
  return await makeAuthenticatedRequest(url, { method: "PUT", body: JSON.stringify(requestBody) });
};

// Fetch pipeline status for name completion (latest version)
export const fetchNameCompletionStatus = async (existingUserData = null, selectedWorkspace = null, authIdClient = null): Promise<NameCompletionStatusResponse> => {
  let userData, idClient;
  if (existingUserData) {
    userData = existingUserData;
    idClient = extractClientId(userData, selectedWorkspace, authIdClient);
  } else {
    userData = await fetchUserData(true);
    idClient = extractClientId(userData, selectedWorkspace, authIdClient);
  }
  if (!idClient) throw new Error("Client ID not found in user data");
  const url = `${getApiUrl()}/${idClient}/name_completion/status`;
  return await makeAuthenticatedRequest(url, { method: "GET" });
};

// Enhanced axios utility with token management and retry logic
const makeAuthenticatedRequest = async (url, options: any = {}, retries = 3) => {
  try {
    // Always ensure we have a valid token before making the request
    const token = await getAuthToken();

    const headers = {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
      ...getApiSecurityHeaders(), // Add security headers
      ...options.headers,
    };

    const requestConfig: any = {
      url,
      headers,
      method: options.method || "GET",
      ...options,
    };

    // Handle data/body for axios
    if (options.body) {
      requestConfig.data = JSON.parse(options.body);
      delete requestConfig.body;
    }
    const response = await axios(requestConfig);

    // Validate security headers in development/dev
    if (import.meta.env.VITE_ENVIRONMENT === "development" || import.meta.env.VITE_ENVIRONMENT === "dev") {
      validateSecurityHeaders(response);
    }

    return response.data;
  } catch (error) {
    console.error("Request failed:", error);
    
    // If this is an HTTP error (not a network error), preserve the status code
    if (error.response && error.response.status) {
      const httpError: any = new Error(error.message || `HTTP ${error.response.status} error`);
      httpError.code = error.response.status.toString();
      httpError.status = error.response.status;
      httpError.response = error.response;
      
      // Don't retry on client errors (4xx), just throw immediately
      if (error.response.status >= 400 && error.response.status < 500) {
        throw httpError;
      }
    }
    
    if (retries <= 0) {
      const maxRetriesError: any = new Error(
        "Max retries reached. Could not complete the request. Last error: " +
        error.message
      );
      // Preserve status code if available
      if (error.response && error.response.status) {
        maxRetriesError.code = error.response.status.toString();
        maxRetriesError.status = error.response.status;
      }
      throw maxRetriesError;
    }

    // Try to refresh token and retry
    try {
      await refreshAuthToken();
      // Retry the request with the new token
      return await makeAuthenticatedRequest(url, options, retries - 1);
    } catch (tokenError) {
      throw new Error(tokenError.message);
    }
  }
};

